export class BarChartGroup {
  constructor(element, config = {}) {
    this.element = element;
    this.config = {
      placeholderColor: config.placeholderColor || "#f5f5f5",
      fillerColor: config.fillerColor || "#ff4444",
      animationDuration: (config.animationDuration || 0.6) * 1000,
      animationDelay: (config.animationDelay || 0.1) * 1000,
      counterDuration: (config.counterDuration || 1.5) * 1000,
      type: config.type || "horizontal",
      // Add intersection observer options
      intersectionThreshold: config.intersectionThreshold || 0.2,
      intersectionRootMargin: config.intersectionRootMargin || "50px",
    };

    // Track animation state
    this.hasAnimated = false;
    this.chartElements = [];

    this.setupIntersectionObserver();
    this.init();
  }

  setupIntersectionObserver() {
    const options = {
      root: null,
      rootMargin: this.config.intersectionRootMargin,
      threshold: this.config.intersectionThreshold
    };

    this.observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting && !this.hasAnimated) {
          this.startAnimations();
          this.hasAnimated = true;
          this.observer.disconnect();
        }
      });
    }, options);
  }

  init() {
    this.charts = this.element.querySelectorAll("[data-chart]");
    const maxValue = parseFloat(this.element.getAttribute("data-chart-max")) || 100;

    this.charts.forEach((chart, index) => {
      const elements = this.createChartElement(chart, index, maxValue);
      this.chartElements.push(elements);
    });

    // Start observing the container element
    this.observer.observe(this.element);
  }

  createChartElement(chart, index, maxValue) {
    const value = parseFloat(chart.getAttribute("data-chart"));
    const percentage = (value / maxValue) * 100;

    const placeholder = document.createElement("div");
    placeholder.className = "chart-placeholder";
    placeholder.style.backgroundColor = this.config.placeholderColor;

    const filler = document.createElement("div");
    filler.className = "chart-filler";
    filler.style.background = this.config.fillerColor;
    filler.style.transitionDuration = `${this.config.animationDuration}ms`;

    // Set initial state
    if (this.config.type === "vertical") {
      filler.style.height = "0%";
    } else {
      filler.style.width = "0%";
    }

    chart.style.setProperty("--p", `${percentage}%`);

    chart.appendChild(placeholder);
    chart.appendChild(filler);

    const valueElement = chart.parentElement.querySelector(".chart-value");
    if (valueElement) {
      valueElement.textContent = "0%";
    }

    // Return the elements we'll need to animate
    return {
      filler,
      valueElement,
      percentage,
      value
    };
  }

  startAnimations() {
    this.chartElements.forEach((elements, index) => {
      const { filler, valueElement, percentage, value } = elements;

      setTimeout(() => {
        if (this.config.type === "vertical") {
          filler.style.height = `${percentage}%`;
        } else {
          filler.style.width = `${percentage}%`;
        }

        if (valueElement) {
          this.animateCounter(0, value, this.config.counterDuration, valueElement);
        }
      }, this.config.animationDelay * index);
    });
  }

  animateCounter(start, end, duration, element) {
    const startTimestamp = performance.now();

    const updateCounter = (currentTimestamp) => {
      const elapsed = currentTimestamp - startTimestamp;
      const progress = Math.min(elapsed / duration, 1);

      const easeOutQuad = (t) => t * (2 - t);
      const easedProgress = easeOutQuad(progress);

      const currentValue = Math.round(start + (end - start) * easedProgress);
      element.textContent = `${currentValue}%`;

      if (progress < 1) {
        requestAnimationFrame(updateCounter);
      }
    };

    requestAnimationFrame(updateCounter);
  }

  dispose() {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }

    if (this.charts) {
      this.charts.forEach((chart) => {
        chart.remove();
      });
      this.charts = null;
    }

    this.chartElements = null;

    if (this.element) {
      this.element.remove();
      this.element = null;
    }

    this.config = null;
  }
}
